import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, FileText, Settings, LogOut, Images, Inbox, Plus, TrendingUp } from "lucide-react";
import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PushToggle from "@/components/PushToggle";

const links = [
  { to: "/admin", icon: LayoutDashboard, label: "Painel", end: true, testid: "nav-dashboard", counterKey: "comprovantes" },
  { to: "/admin/orcamentos", icon: FileText, label: "Orçamentos", testid: "nav-orcamentos" },
  { to: "/admin/solicitacoes", icon: Inbox, label: "Solicitações", testid: "nav-solicitacoes", counterKey: "solicitacoes" },
  { to: "/admin/clientes", icon: Users, label: "Clientes", testid: "nav-clientes" },
  { to: "/admin/portfolio", icon: Images, label: "Portfólio", testid: "nav-portfolio" },
];

export default function AdminLayout() {
  const loc = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [novasSolicitacoes, setNovasSolicitacoes] = useState(0);
  const [novosComprovantes, setNovosComprovantes] = useState(0);

  useEffect(() => {
    api.get("/configuracoes").then(({ data }) => setCfg(data)).catch(() => {});
  }, []);

  // Contagem de solicitações não lidas — refetch em cada mudança de rota
  useEffect(() => {
    api.get("/solicitacoes", { params: { lida: false } })
      .then(({ data }) => setNovasSolicitacoes(Array.isArray(data) ? data.length : 0))
      .catch(() => {});
    api.get("/comprovantes-pix/pendentes-count")
      .then(({ data }) => setNovosComprovantes(data?.count || 0))
      .catch(() => {});
  }, [loc.pathname]);

  const logoPath = cfg?.logo ? cfg.logo.replace("/api/files/", "") : null;
  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const Brand = (
    <div className="flex items-center gap-3">
      {logoPath ? (
        <img src={fileUrl(logoPath)} alt="BBH" className="w-11 h-11 object-contain" />
      ) : (
        <div className="w-11 h-11 rounded-md bg-[#14243D] text-white flex items-center justify-center font-heading font-bold">
          B
        </div>
      )}
      <div>
        <div className="font-heading font-bold text-lg leading-none">{cfg?.nome_empresa || "BBH"}</div>
        <div className="text-[10px] tracking-widest text-zinc-500 uppercase">Orçamentos</div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-[#FAFAFA]" style={{ minHeight: "100dvh" }}>
      {/* Mobile top header */}
      <header
        className="md:hidden sticky top-0 z-30 bg-white border-b border-zinc-200 px-4 pb-3 flex items-center justify-between gap-2"
        data-testid="mobile-header"
        style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 0.75rem)" }}
      >
        {Brand}
        <div className="flex items-center gap-1">
          <PushToggle />
          <NavLink
            to="/admin/relatorios"
            data-testid="mobile-relatorios-btn"
            aria-label="Relatórios"
            className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-zinc-200 text-zinc-600 hover:text-zinc-900"
          >
            <TrendingUp className="w-4 h-4" />
          </NavLink>
          <NavLink
            to="/admin/configuracoes"
            data-testid="mobile-config-btn"
            aria-label="Configurações"
            className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-zinc-200 text-zinc-600 hover:text-zinc-900"
          >
            <Settings className="w-4 h-4" />
          </NavLink>
          {user && (
            <button
              onClick={handleLogout}
              data-testid="mobile-logout-btn"
              aria-label="Sair"
              className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-zinc-200 text-zinc-500 hover:text-rose-600 hover:border-rose-200"
            >
              <LogOut className="w-4 h-4" />
            </button>
          )}
        </div>
      </header>

      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:w-64 md:min-h-screen md:border-r border-zinc-200 bg-white md:flex-col">
        <div className="p-5 border-b border-zinc-200 flex items-center gap-3 w-full">{Brand}</div>
        <nav className="flex flex-col p-3 gap-1 flex-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={l.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                  isActive
                    ? "bg-zinc-900 text-white"
                    : "text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100"
                }`
              }
            >
              <l.icon className="w-4 h-4" />
              <span className="flex-1">{l.label}</span>
              {l.counterKey === "solicitacoes" && novasSolicitacoes > 0 && (
                <span
                  data-testid="sidebar-badge-solicitacoes"
                  className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-rose-500 text-white text-[10px] font-bold"
                >
                  {novasSolicitacoes > 99 ? "99+" : novasSolicitacoes}
                </span>
              )}
              {l.counterKey === "comprovantes" && novosComprovantes > 0 && (
                <span
                  data-testid="sidebar-badge-comprovantes"
                  className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-white text-[10px] font-bold"
                >
                  {novosComprovantes > 99 ? "99+" : novosComprovantes}
                </span>
              )}
            </NavLink>
          ))}
          <NavLink
            to="/admin/relatorios"
            data-testid="nav-relatorios"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                isActive
                  ? "bg-zinc-900 text-white"
                  : "text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100"
              }`
            }
          >
            <TrendingUp className="w-4 h-4" />
            <span>Relatórios</span>
          </NavLink>
          <NavLink
            to="/admin/configuracoes"
            data-testid="nav-configuracoes"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                isActive
                  ? "bg-zinc-900 text-white"
                  : "text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100"
              }`
            }
          >
            <Settings className="w-4 h-4" />
            <span>Configurações</span>
          </NavLink>
        </nav>
        <div className="p-3 border-t border-zinc-200 space-y-2">
          <PushToggle variant="full" />
          <NavLink
            to="/admin/orcamentos?novo=1"
            data-testid="sidebar-novo-orcamento-btn"
            className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-md bbh-btn-primary text-sm font-semibold"
          >
            <Plus className="w-4 h-4" /> Novo Orçamento
          </NavLink>
          {user && (
            <div className="pt-2 border-t border-zinc-100">
              <div className="px-2 py-1.5">
                <div className="text-[10px] text-zinc-400 uppercase tracking-wide">Admin</div>
                <div className="text-xs font-medium text-zinc-700 truncate" data-testid="admin-email">{user.email}</div>
              </div>
              <button
                onClick={handleLogout}
                data-testid="logout-btn"
                className="w-full flex items-center gap-2 px-2 py-2 rounded-md text-xs text-zinc-500 hover:text-rose-600 hover:bg-rose-50"
              >
                <LogOut className="w-3.5 h-3.5" /> Sair
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 min-w-0 md:pb-0" style={{ paddingBottom: "calc(6rem + env(safe-area-inset-bottom, 0px))" }}>
        <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8" key={loc.pathname}>
          <Outlet />
        </div>
      </main>

      {/* Mobile bottom nav */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-zinc-200 shadow-[0_-6px_20px_rgba(0,0,0,0.06)]"
        data-testid="mobile-bottom-nav"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        <div className="grid grid-cols-5">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`mobile-${l.testid}`}
              className={({ isActive }) =>
                `relative flex flex-col items-center justify-center gap-1 py-2.5 text-[11px] font-medium ${
                  isActive ? "text-[#14243D]" : "text-zinc-500"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <div className="relative">
                    <l.icon className={`w-5 h-5 ${isActive ? "stroke-[2.5]" : ""}`} />
                    {l.counterKey === "solicitacoes" && novasSolicitacoes > 0 && (
                      <span
                        data-testid="mobile-badge-solicitacoes"
                        className="absolute -top-1.5 -right-2 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[9px] font-bold leading-none"
                      >
                        {novasSolicitacoes > 9 ? "9+" : novasSolicitacoes}
                      </span>
                    )}
                    {l.counterKey === "comprovantes" && novosComprovantes > 0 && (
                      <span
                        data-testid="mobile-badge-comprovantes"
                        className="absolute -top-1.5 -right-2 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-amber-500 text-white text-[9px] font-bold leading-none"
                      >
                        {novosComprovantes > 9 ? "9+" : novosComprovantes}
                      </span>
                    )}
                  </div>
                  <span>{l.label}</span>
                  <span
                    className={`h-0.5 w-6 rounded-full ${isActive ? "bg-[#14243D]" : "bg-transparent"}`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
